import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Optional,
  Renderer2,
  SkipSelf
} from '@angular/core';
import { Subscription } from 'rxjs';

import { TabView } from 'primeng/tabview';

/** Nombre del parámetro de URL según la profundidad del tabView: tab, tab2, tab3... */
function paramNameForDepth(depth: number): string {
  return depth === 0 ? 'tab' : `tab${depth + 1}`;
}

/** Profundidad que representa un parámetro de URL, o -1 si no es un parámetro de pestaña. */
function depthForParamName(name: string): number {
  if (name === 'tab') {
    return 0;
  }
  const match = /^tab([2-9]|\d{2,})$/.exec(name);
  return match ? Number(match[1]) - 1 : -1;
}

/**
 * Hace que las subsecciones de un `p-tabView` (Ventas Dirección, Actualizar datos,
 * Ajustes Manuales, ...) se comporten como enlaces normales: ctrl/cmd + click,
 * click central y "Abrir enlace en una pestaña nueva" abren la subsección en otra
 * pestaña del navegador en lugar de cambiar la pestaña actual.
 *
 * La subsección se codifica en la URL (`?tab=2`; los tabView anidados usan
 * `tab2`, `tab3`, ...) y se restaura al cargar la página. El click normal sigue
 * siendo navegación interna porque PrimeNG cancela el evento en `TabView.open()`.
 *
 * Se aplica automáticamente a todos los `p-tabView` de los módulos que importan
 * este directive (ver SharedPrimeNgModule).
 */
@Directive({
  selector: 'p-tabView',
  standalone: true
})
export class TabDeepLinkDirective implements AfterViewInit, OnDestroy {
  private panelsSubscription?: Subscription;

  constructor(
    private tabView: TabView,
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    @Optional() @SkipSelf() private parentTabs: TabDeepLinkDirective | null
  ) {}

  ngAfterViewInit(): void {
    const host = this.el.nativeElement;
    // Fase de captura: hay que adelantarse al handler de PrimeNG, que llama a
    // preventDefault() y cancelaría la apertura de la pestaña del navegador.
    host.addEventListener('click', this.onLinkClick, true);
    host.addEventListener('auxclick', this.onLinkClick, true);
    // Los href dependen de la pestaña activa de los tabView superiores, así que
    // se recalculan justo antes de cualquier interacción.
    host.addEventListener('pointerdown', this.refreshLinks, true);
    host.addEventListener('contextmenu', this.refreshLinks, true);

    this.panelsSubscription = this.tabView.tabPanels?.changes.subscribe(() =>
      setTimeout(() => this.refreshLinks())
    );

    setTimeout(() => {
      this.refreshLinks();
      this.restoreFromUrl();
    });
  }

  ngOnDestroy(): void {
    const host = this.el.nativeElement;
    host.removeEventListener('click', this.onLinkClick, true);
    host.removeEventListener('auxclick', this.onLinkClick, true);
    host.removeEventListener('pointerdown', this.refreshLinks, true);
    host.removeEventListener('contextmenu', this.refreshLinks, true);
    this.panelsSubscription?.unsubscribe();
  }

  private get depth(): number {
    return this.parentTabs ? this.parentTabs.depth + 1 : 0;
  }

  private get paramName(): string {
    return paramNameForDepth(this.depth);
  }

  private get activeIndex(): number {
    return (this.tabView.tabs ?? []).findIndex(tab => tab.selected);
  }

  private onLinkClick = (event: MouseEvent): void => {
    const link = this.ownLinkOf(event);
    if (!link || !link.hasAttribute('href')) {
      return;
    }
    const opensNewTab = event.ctrlKey || event.metaKey || event.button === 1;
    if (!opensNewTab) {
      return;
    }
    // Sin preventDefault: el navegador abre el enlace en otra pestaña.
    event.stopPropagation();
  };

  private refreshLinks = (): void => {
    for (const link of this.ownNavLinks()) {
      const index = Number(link.getAttribute('data-pc-index'));
      const disabled = link.parentElement?.getAttribute('data-p-disabled') === 'true';
      if (!Number.isInteger(index) || disabled) {
        this.renderer.removeAttribute(link, 'href');
      } else {
        this.renderer.setAttribute(link, 'href', this.buildUrl(index));
      }
    }
  };

  /** Cabeceras de este tabView, excluyendo las de tabView anidados. */
  private ownNavLinks(): HTMLAnchorElement[] {
    const links = this.el.nativeElement.querySelectorAll<HTMLAnchorElement>('a.p-tabview-nav-link');
    return Array.from(links).filter(link => link.closest('p-tabView') === this.el.nativeElement);
  }

  private ownLinkOf(event: Event): HTMLAnchorElement | null {
    const target = event.target as HTMLElement | null;
    if (!target || target.closest('.p-tabview-close')) {
      return null;
    }
    const link = target.closest<HTMLAnchorElement>('a.p-tabview-nav-link');
    return link && link.closest('p-tabView') === this.el.nativeElement ? link : null;
  }

  private buildUrl(index: number): string {
    const url = new URL(window.location.href);
    // Los niveles superiores conservan la subsección que se está viendo.
    for (let ancestor = this.parentTabs; ancestor; ancestor = ancestor.parentTabs) {
      this.writeParam(url, ancestor.paramName, ancestor.activeIndex);
    }
    this.writeParam(url, this.paramName, index);
    // Los niveles más profundos no aplican a la subsección de destino.
    const ownDepth = this.depth;
    Array.from(url.searchParams.keys())
      .filter(name => depthForParamName(name) > ownDepth)
      .forEach(name => url.searchParams.delete(name));
    return url.toString();
  }

  private writeParam(url: URL, name: string, index: number): void {
    if (index > 0) {
      url.searchParams.set(name, String(index));
    } else {
      url.searchParams.delete(name);
    }
  }

  private restoreFromUrl(): void {
    const raw = new URL(window.location.href).searchParams.get(this.paramName);
    if (raw === null) {
      return;
    }
    const index = Number(raw);
    const tab = Number.isInteger(index) ? (this.tabView.tabs ?? [])[index] : undefined;
    if (!tab || tab.disabled || tab.selected) {
      return;
    }
    // open() emite onChange, que es de lo que dependen los componentes para
    // renderizar el contenido de la subsección.
    this.tabView.open(null as unknown as Event, tab);
    this.tabView.cd.markForCheck();
  }
}
